import { useMemo, useState } from 'react'
import { MultiSelect } from '@forwardflow/ui-v2'
import { useDebouncedValue } from '@forwardflow/hooks/customHooks/useDebouncedValue'
import { useAppStore } from '@forwardflow/store/appStore'
import { useGetPeopleTags } from '@forwardflow/queries/onboarding.queries'
import { isFreeFlow } from '../../utils/demo-mode'
import { IOption } from '../../hooks/useAudienceVocabulary'

/**
 * A tag MultiSelect that searches Follow Up Boss server-side as the user
 * types. Organizations carry thousands of tags while the vocabulary fetch
 * holds only the first page, so a client-side filter over it hides the rest.
 */
export const TagPicker = ({
   baseOptions,
   value,
   onChange,
   placeholder,
   label,
   className,
   testId,
}: {
   baseOptions: IOption[]
   value: string[]
   onChange: (v: string[]) => void
   placeholder?: string
   label?: string
   className?: string
   testId?: string
}) => {
   const [search, setSearch] = useState('')
   const [debounced] = useDebouncedValue(search, 300)
   const { currentOrganization } = useAppStore()
   const orgId = isFreeFlow() ? '' : currentOrganization || ''
   const term = debounced.trim()
   const { data, isLoading } = useGetPeopleTags(
      orgId,
      1,
      100,
      Boolean(orgId && term),
      term
   )
   const options = useMemo(() => {
      const seen = new Set<string>()
      const out: IOption[] = []
      const push = (v: string) => {
         if (v && !seen.has(v)) {
            seen.add(v)
            out.push({ value: v, label: v })
         }
      }
      baseOptions.forEach((o) => push(o.value))
      const searched = (data?.data || []) as Array<{ tagName: string }>
      searched.forEach((t) => push(t.tagName))
      value.forEach(push)
      return out
   }, [baseOptions, data, value])
   return (
      <MultiSelect
         size="sm"
         className={className}
         label={label}
         data={options}
         value={value}
         onChange={onChange}
         placeholder={placeholder}
         searchable
         searchValue={search}
         onSearchChange={setSearch}
         nothingFoundMessage={
            term && isLoading ? 'Searching…' : 'No matching tags'
         }
         data-testid={testId}
      />
   )
}
